import { useCallback, useRef, useState } from "react";
import { toast } from "@/components/ui/sonner";
import { boletosApi, Parcelas } from "@/lib/api";
import { isBoletoAvailabilityEnabled } from "@/features/boletoAvailabilityFeature";

interface UseBoletoViewerOptions {
    /**
     * Chamado quando o Dynamics confirma que o boleto está indisponível — a tela usa para recarregar a
     * listagem por trás dos panos (o boleto pode ter sido substituído por outro, com novo `recIdBoleto`).
     */
    onUnavailable?: () => void;
}

/**
 * Fluxo de "ver boleto" compartilhado por Financial, ClientDetails e OrderDetails.
 * Ver .claude/docs/frontend_boleto_availability.md, seção 3.
 *
 * Com `recIdBoleto`, confirma no Dynamics que o boleto ainda está disponível e abre o diálogo com o
 * `billet` devolvido agora — nunca com o HTML da listagem, que pode estar desatualizado.
 * Sem `recIdBoleto` (dados antigos) ou com `isBoletoAvailabilityEnabled` desligada, mantém o comportamento anterior.
 *
 * Sem React Query de propósito: a consulta é feita a cada clique e a resposta (~140KB) não deve ficar em cache.
 */
export function useBoletoViewer({ onUnavailable }: UseBoletoViewerOptions = {}) {
    const [boleto, setBoleto] = useState<Parcelas | null>(null);
    const [currentGeneratingBoletoId, setCurrentGeneratingBoletoId] = useState<number | null>(null);
    // Boletos que o Dynamics confirmou como indisponíveis: a grid esconde a ação de ver só para eles.
    // A chave é o recIdBoleto — um boleto substituído ganha outro RecId e volta a ter a ação.
    const [unavailableBoletoIds, setUnavailableBoletoIds] = useState<ReadonlySet<number>>(() => new Set());
    // Só a consulta mais recente pode abrir o diálogo: uma resposta lenta de outro boleto não sobrescreve a atual.
    const latestRequestRef = useRef(0);

    const openBoleto = useCallback(async (parcela: Parcelas) => {
        const requestId = ++latestRequestRef.current;
        // Setado antes do await: o `disabled={isGenerating}` do botão bloqueia cliques repetidos.
        setCurrentGeneratingBoletoId(parcela.parmJournalTransRecId);

        const { recIdBoleto } = parcela;
        if (!isBoletoAvailabilityEnabled || !recIdBoleto) {
            setBoleto(parcela);
            return;
        }

        try {
            const { data } = await boletosApi.getBilletInvoice(recIdBoleto);
            if (requestId !== latestRequestRef.current) return;

            if (!data.disponivel || !data.billet) {
                toast.error("Boleto indisponível", {
                    description: "Este boleto não está mais disponível. Fale com o financeiro se precisar de uma nova via.",
                });
                setUnavailableBoletoIds(prev => new Set(prev).add(recIdBoleto));
                setCurrentGeneratingBoletoId(null);
                onUnavailable?.();
                return;
            }

            setBoleto({ ...parcela, parmBase64PdfBoleto: data.billet });
        } catch {
            if (requestId !== latestRequestRef.current) return;
            toast.error("Não foi possível verificar o boleto", { description: "Tente novamente em instantes." });
            setCurrentGeneratingBoletoId(null);
        }
    }, [onUnavailable]);

    const closeBoleto = useCallback(() => {
        setBoleto(null);
        setCurrentGeneratingBoletoId(null);
    }, []);

    return { boleto, currentGeneratingBoletoId, unavailableBoletoIds, openBoleto, closeBoleto };
}
